import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, AlertCircle, Bot } from "lucide-react";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { useSort, type SortOption } from "~/hooks/useSort";
import { useViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { SortMenu } from "~/pages/manifold-nextgen/components/SortMenu";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { useCanUseAIAgents } from "~/pages/manifold-nextgen/hooks/useCanUseAIAgents";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { Agent, AgentList as AgentListType, agentListPath, createAgent } from "~/services/agents";
import { AgentCard } from "./AgentCard";

const AGENT_SORT_OPTIONS: SortOption<Agent>[] = [
  {
    key: "display_name",
    label: "Name",
    kind: "alpha",
    accessor: (a) => a.display_name,
    defaultDirection: "asc",
  },
];

export const Agents = () => {
  const { data, error, isLoading } = useFetcher<AgentListType>(agentListPath());
  const [search, setSearch] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const canCreateAgent = useCanUseAIAgents();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [viewMode, setViewMode] = useViewMode("nextgen-view-mode");

  const handleCreateAgent = useCallback(async () => {
    setIsCreating(true);
    try {
      const agent = await createAgent();
      showSuccess("Agent created");
      navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`);
    } catch (err) {
      console.error(err);
      showError("Failed to create agent");
    } finally {
      setIsCreating(false);
    }
  }, [navigate, showSuccess, showError]);

  const headerActions = useMemo(
    () =>
      canCreateAgent
        ? ([
            <Button
              key="new-agent"
              size="sm"
              className="text-xs"
              onClick={handleCreateAgent}
              loading={isCreating}
            >
              <Plus aria-hidden="true" />
              New Agent
            </Button>,
          ] as [ReactNode])
        : ([] as []),
    [canCreateAgent, handleCreateAgent, isCreating]
  );

  useNextGenHeader({ actions: headerActions });

  const filtered = (() => {
    if (!data?.items) {
      return [];
    }
    if (!search.trim()) {
      return data.items;
    }
    const lower = search.toLowerCase();
    return data.items.filter(
      (a) =>
        a.display_name.toLowerCase().includes(lower) || a.description.toLowerCase().includes(lower)
    );
  })();

  const { sortKey, direction, activeOption, setSortKey, toggleDirection, sorted } = useSort(
    "nextgen-agent-sort",
    AGENT_SORT_OPTIONS,
    filtered
  );

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-lg">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search agents"
            className={cn(
              "h-8 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1",
              "text-sm placeholder:text-muted-foreground shadow-xs",
              "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
              "transition-[color,box-shadow]"
            )}
          />
        </div>
        <div className="flex items-center gap-2">
          <SortMenu
            options={AGENT_SORT_OPTIONS}
            sortKey={sortKey}
            direction={direction}
            activeLabel={activeOption.label}
            onSelectKey={setSortKey}
            onToggleDirection={toggleDirection}
          />
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {isLoading && viewMode === "list" && (
        <ListLoadingSkeleton
          aria-label="Loading agents"
          showAvatar
          columns={[{ width: "w-40", height: "h-5", rounded: true }, { width: "w-36" }]}
        />
      )}
      {isLoading && viewMode === "card" && (
        <div
          role="status"
          aria-label="Loading agents"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-border bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
        >
          <AlertCircle className="size-8" aria-hidden="true" />
          <p className="text-sm font-medium">Failed to load agents</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Bot className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">
            {search ? "No agents match your search" : "No agents found"}
          </p>
          {search && <p className="text-xs text-muted-foreground">Try a different search term</p>}
        </div>
      )}

      {!isLoading && !error && filtered.length > 0 && viewMode === "card" && (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
          {sorted.map((agent) => (
            <li key={agent.id} className="flex flex-col">
              <AgentCard agent={agent} view="card" />
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !error && filtered.length > 0 && viewMode === "list" && (
        <DividedList
          items={sorted}
          renderItem={(agent) => <AgentCard agent={agent} view="list" />}
        />
      )}
    </div>
  );
};
